// CastYardImportView.tsx — 2D plan of a cv-cad cast-attach yard (CV-808).
// cv-cad TECT bundles are "one panel template + N stamped placements": the
// placements live in site.layout.inserts[] (cv-attach blocks, inches/radians)
// or site.panels[] (erected top-up placements, feet/degrees), not in
// floors→elevations→panels. CastLayoutView's floor-driven pipeline can never
// render them, so this view draws the yard directly from the site data.

import { useMemo } from 'react';
import type { PanelData, SiteData, CastLayoutInsert, CastLayoutMark } from '../types/panelvar';

export interface YardRect {
  key:   string;
  label: string;
  /** Polygon corners in world inches, CAD Y-up. */
  pts:   [number, number][];
}

function rotatedRect(x: number, y: number, w: number, h: number, r: number): [number, number][] {
  const cos = Math.cos(r), sin = Math.sin(r);
  const wx = w * cos, wy = w * sin;   // along-width vector
  const hx = -h * sin, hy = h * cos;  // along-height vector
  return [
    [x, y],
    [x + wx, y + wy],
    [x + wx + hx, y + wy + hy],
    [x + hx, y + hy],
  ];
}

export function buildYard(site: SiteData, panels: PanelData[]): { rects: YardRect[]; marks: CastLayoutMark[]; source: string } {
  // Single-template projects (the TECT shape): every placement stamps the
  // same panel, so its dims apply to inserts whose block name has no match.
  const template = panels.length === 1 ? panels[0] : undefined;
  const byMark = new Map(panels.map(p => [p.mpvar.panelMark.replace(/[^a-z0-9]/gi, '').toLowerCase(), p]));
  const dimsFor = (blockName: string): { w: number; h: number } | null => {
    const p = byMark.get(blockName.replace(/[^a-z0-9]/gi, '').toLowerCase()) ?? template ?? panels[0];
    if (!p || !p.mpvar.width || !p.mpvar.height) return null;
    return { w: p.mpvar.width, h: p.mpvar.height };
  };

  // cv-cad partial exports can zero out the template's width/height (the TECT
  // fixture does). The cast-attach mark sits at the panel's TRUE rotated
  // center (locked cv-attach spec: mk = insert + R(rot)·(w/2, h/2)), so
  // inverting that formula recovers the real dims from the bundle itself.
  const dimsFromMark = (ins: CastLayoutInsert, mark: CastLayoutMark | undefined): { w: number; h: number } | null => {
    if (!mark) return null;
    const dx = mark.x - ins.x, dy = mark.y - ins.y;
    const cos = Math.cos(ins.rotation), sin = Math.sin(ins.rotation);
    const hw =  cos * dx + sin * dy;
    const hh = -sin * dx + cos * dy;
    if (!(hw > 0) || !(hh > 0)) return null;
    return { w: 2 * hw, h: 2 * hh };
  };

  const layout = site.layout;
  if (layout?.present && layout.inserts.length > 0) {
    // Marks pair with inserts by panel NUMBER, not array index — the marks
    // array arrives lexically sorted (P1, P10, P11, … P2), so "TECT012"
    // matches mark "P12" via the trailing digits.
    const markByNum = new Map<number, CastLayoutMark>();
    for (const m of layout.marks ?? []) {
      const n = parseInt(m.text.replace(/\D/g, ''), 10);
      if (Number.isFinite(n)) markByNum.set(n, m);
    }
    const rects: YardRect[] = [];
    layout.inserts.forEach((ins, i) => {
      const num = parseInt(ins.name.replace(/\D/g, ''), 10);
      const fromPanel = dimsFor(ins.name);
      const d = (fromPanel
        ? { w: fromPanel.w * (ins.xscale || 1), h: fromPanel.h * (ins.yscale || 1) }
        : null) ?? dimsFromMark(ins, markByNum.get(num));
      if (!d) return;
      rects.push({
        key:   `${ins.name}-${i}`,
        label: ins.name,
        // Insert point is the block base = panel corner; rotation in radians.
        pts:   rotatedRect(ins.x, ins.y, d.w, d.h, ins.rotation),
      });
    });
    return { rects, marks: layout.marks ?? [], source: layout.source || 'cv-attach' };
  }

  // Fallback: erected top-up placements — center-anchored, feet, degrees.
  const byId = new Map(panels.map(p => [p.id, p]));
  const rects: YardRect[] = [];
  site.panels.forEach((pl, i) => {
    const p = byId.get(pl.panelId) ?? template ?? panels[0];
    if (!p || !p.mpvar.width || !p.mpvar.height) return;
    const w = p.mpvar.width, h = p.mpvar.thickness || 8;  // plan view: width × thickness
    const r = ((pl.rotation ?? pl.angle ?? 0) * Math.PI) / 180;
    const cx = pl.x * 12, cy = pl.y * 12;
    const cos = Math.cos(r), sin = Math.sin(r);
    // Shift from center back to the corner rotatedRect expects.
    const x0 = cx - (w / 2) * cos - (h / 2) * -sin;
    const y0 = cy - (w / 2) * sin - (h / 2) * cos;
    rects.push({ key: `${pl.panelId}-${i}`, label: p.mpvar.panelMark, pts: rotatedRect(x0, y0, w, h, r) });
  });
  return { rects, marks: [], source: 'site plan' };
}

export interface CastYardImportViewProps {
  site:   SiteData;
  panels: PanelData[];
}

export default function CastYardImportView({ site, panels }: CastYardImportViewProps) {
  const { rects, marks, source } = useMemo(() => buildYard(site, panels), [site, panels]);

  const view = useMemo(() => {
    let minX = Infinity, maxX = -Infinity, minY = Infinity, maxY = -Infinity;
    for (const r of rects) for (const [px, py] of r.pts) {
      if (px < minX) minX = px; if (px > maxX) maxX = px;
      if (py < minY) minY = py; if (py > maxY) maxY = py;
    }
    for (const m of marks) {
      if (m.x < minX) minX = m.x; if (m.x > maxX) maxX = m.x;
      if (m.y < minY) minY = m.y; if (m.y > maxY) maxY = m.y;
    }
    if (!Number.isFinite(minX)) { minX = 0; maxX = 100; minY = 0; maxY = 100; }
    const margin = Math.max(maxX - minX, maxY - minY) * 0.05 + 24;
    return { minX: minX - margin, maxY: maxY + margin, w: (maxX - minX) + margin * 2, h: (maxY - minY) + margin * 2 };
  }, [rects, marks]);

  // CAD is Y-up, SVG is Y-down — flip Y here.
  const sx = (x: number) => x - view.minX;
  const sy = (y: number) => view.maxY - y;
  const markFontSize = Math.max(view.w, view.h) / 80;

  if (rects.length === 0) {
    return (
      <div className="site-view" style={{ alignItems: 'center', justifyContent: 'center', flex: 1 }}>
        <div style={{ textAlign: 'center', maxWidth: 480, padding: 32 }}>
          <h2 style={{ marginBottom: 12 }}>Cast Layout</h2>
          <p style={{ color: 'var(--text-muted, #888)', lineHeight: 1.5 }}>
            The imported site plan has placements but no panel with valid dimensions to draw them with.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="site-view" style={{ flex: 1, display: 'flex', flexDirection: 'column', minHeight: 0 }}>
      <div style={{ padding: '6px 12px', fontSize: 12, color: 'var(--text-muted, #888)', flexShrink: 0 }}>
        Cast yard imported from cv-cad ({source}) — {rects.length} panels
      </div>
      <div style={{ flex: 1, minHeight: 0, overflow: 'auto' }}>
        <svg
          viewBox={`0 0 ${view.w} ${view.h}`}
          style={{ width: '100%', height: '100%', display: 'block', background: 'var(--bg-canvas, #1a1a1a)' }}
          data-testid="cast-yard-import-svg"
        >
          {rects.map(r => (
            <polygon
              key={r.key}
              points={r.pts.map(([px, py]) => `${sx(px)},${sy(py)}`).join(' ')}
              fill="rgba(100, 160, 220, 0.15)"
              stroke="#64a0dc"
              strokeWidth={Math.max(view.w, view.h) / 800}
            >
              <title>{r.label}</title>
            </polygon>
          ))}
          {marks.map((m, i) => (
            <text
              key={`mark-${i}`}
              x={sx(m.x)}
              y={sy(m.y)}
              fontSize={markFontSize}
              fill="var(--text, #ddd)"
              textAnchor="middle"
              dominantBaseline="middle"
            >
              {m.text}
            </text>
          ))}
        </svg>
      </div>
    </div>
  );
}
